// Copyright (c) StrangeBee 2023

import React, { ReactElement, useContext, useMemo } from 'react';
import { useLocalMessage } from '@Helpers/';
import { FormInstance } from 'antd/lib/form';
import { THForm } from '@Molecules/THForm/THForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { UserOrganisationInput, UserType, UserTypeValues } from 'thehive-sdk';
import { GlobalUserOrgSelector } from '../GlobalUserOrgSelector/GlobalUserOrgSelector';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { StatusContext } from '@Context/StatusProvider';
import Form from 'antd/es/form';
import { queryClient } from '@Client';

export interface GlobalUserValuesForm {
    login: string;
    name: string;
    organisations: UserOrganisationInput[];
    type: UserType;
}

interface GlobalUserFormProps {
    form: FormInstance;
    isValid: boolean;
    onFinish(values: GlobalUserValuesForm): void;
    onChange: () => void;
    onCancel: () => void;
}

export const GlobalUserForm = ({ form, isValid, onFinish, onChange, onCancel }: GlobalUserFormProps): ReactElement => {
    const i = useLocalMessage();

    // Start duplicated code from /src/components/organisms/User/UserCreate/UserForm.tsx:54
    queryClient.refetchQueries(['getStatus']); // Ugly way to prevent refetch waiting time

    const { quotas } = useContext(StatusContext);

    const type = Form.useWatch<string>(['type'], form);

    const exceededTypeQuota = useMemo(
        () => (type ? quotas.users[type.toLowerCase()].current >= quotas.users[type.toLowerCase()].quota : false),
        [type, quotas],
    );

    const userTypes = UserTypeValues.reduce((acc, value) => {
        return [...acc, { label: value, value }];
    }, [] as { label: string; value: string }[]);
    // End duplicated code

    return (
        <THForm<GlobalUserValuesForm>
            name="global-user-form"
            onFinish={onFinish}
            form={form}
            onValuesChange={onChange}
            initialValues={{
                type: userTypes?.find(Boolean)?.label,
            }}
        >
            <THForm.Layout
                footer={<FormSubmitButton onCancel={onCancel} disabled={!isValid} footerText={i('general.confirm')} />}
            >
                <THForm.Item
                    name="type"
                    label={i('user.create.form.type.label')}
                    help={i('user.create.form.type.help')}
                >
                    <THSelect options={userTypes} />
                </THForm.Item>

                <THForm.Item
                    label={i('user.create.form.login.label')}
                    name="login"
                    rules={[{ required: true, type: 'email', message: i('user.create.form.login.required') }]}
                >
                    <THInput placeholder={i('user.create.form.login.placeholder')} />
                </THForm.Item>

                <THForm.Item
                    label={i('user.create.form.name.label')}
                    name="name"
                    rules={[{ required: true, message: i('user.create.form.name.required') }]}
                >
                    <THInput placeholder={i('user.create.form.name.placeholder')} />
                </THForm.Item>

                <THForm.Item
                    name="organisations"
                    label={i('common.organisations')}
                    rules={[
                        {
                            validator: (_, value) => {
                                if (value?.length === 0 || !value) {
                                    return Promise.reject('Should have at least one organisation!');
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <GlobalUserOrgSelector exceededQuota={exceededTypeQuota} />
                </THForm.Item>
            </THForm.Layout>
        </THForm>
    );
};
